import { useEffect, useState } from "react";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import type { TabCreatePayload, TabUpdatePayload } from "@api/tabs";
import { useQuery } from "@tanstack/react-query";
import { t } from "@lib/i18n";
import { Loader2 } from "lucide-react";
import type { Artist } from "@api/artists";
import type { User } from "@api/users";
import { getApiUrl } from "@lib/utils";

const tabSchema = z.object({
  artist_id: z.number().min(1, "Artist is required"),
  title: z.string().min(1, "Title is required").max(255, "Title is too long"),
  body: z.string().min(1, "Body is required"),
  body_rendered: z.string().optional(),
  user_id: z.number().min(1, "User is required"),
});

type TabFormData = z.infer<typeof tabSchema>;

interface TabFormProps {
  initialData?: {
    artist_id: number;
    title: string;
    body: string;
    body_rendered?: string;
    user_id: number;
  };
  onSubmit: (data: TabCreatePayload | TabUpdatePayload) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function TabForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading,
}: TabFormProps) {
  // Fetch all artists by paginating through all pages
  const {
    data: allArtists,
    isLoading: artistsLoading,
    isError: artistsError,
  } = useQuery({
    queryKey: ["all-artists"],
    queryFn: async () => {
      const token = localStorage.getItem("admin_token");
      if (!token) throw new Error("No token");
      const apiUrl = getApiUrl();
      const all: Artist[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(
          `${apiUrl}/artist?token=${token}&page=${page}&page_size=100`
        );
        if (!response.ok) throw new Error("Failed to fetch artists");
        const data = await response.json();
        all.push(...data.list);
        hasMore = page < data.total_pages;
        page++;
      }
      return { list: all };
    },
  });

  // Fetch all users by paginating through all pages
  const {
    data: allUsers,
    isLoading: usersLoading,
    isError: usersError,
  } = useQuery({
    queryKey: ["all-users"],
    queryFn: async () => {
      const token = localStorage.getItem("admin_token");
      if (!token) throw new Error("No token");
      const apiUrl = getApiUrl();
      const all: User[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const response = await fetch(
          `${apiUrl}/user?token=${token}&page=${page}&page_size=100`
        );
        if (!response.ok) throw new Error("Failed to fetch users");
        const data = await response.json();
        all.push(...data.list);
        hasMore = page < data.total_pages;
        page++;
      }
      return { list: all };
    },
  });

  const artists = allArtists;
  const users = allUsers;

  const [artistId, setArtistId] = useState<number>(
    initialData?.artist_id ? Number(initialData.artist_id) : 0
  );
  const [title, setTitle] = useState<string>(initialData?.title || "");
  const [body, setBody] = useState<string>(initialData?.body || "");
  const [bodyRendered, setBodyRendered] = useState<string>(
    initialData?.body_rendered || ""
  );
  const [userId, setUserId] = useState<number>(
    initialData?.user_id ? Number(initialData.user_id) : 0
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Create a minimal form context for Form components (we still use useState for actual state)
  const form = useForm<TabFormData>({
    defaultValues: {
      artist_id: artistId,
      title,
      body,
      body_rendered: bodyRendered,
      user_id: userId,
    },
  });

  // Set form values when initialData changes
  useEffect(() => {
    if (initialData) {
      setArtistId(
        initialData.artist_id !== undefined && initialData.artist_id !== null
          ? Number(initialData.artist_id)
          : 0
      );
      setTitle(initialData.title || "");
      setBody(initialData.body || "");
      setBodyRendered(initialData.body_rendered || "");
      setUserId(
        initialData.user_id !== undefined && initialData.user_id !== null
          ? Number(initialData.user_id)
          : 0
      );
    } else {
      // Reset to empty for create mode
      setArtistId(0);
      setTitle("");
      setBody("");
      setBodyRendered("");
      setUserId(0);
    }
    setErrors({});
  }, [
    initialData?.artist_id,
    initialData?.title,
    initialData?.body,
    initialData?.body_rendered,
    initialData?.user_id,
  ]);

  // Keep form context in sync with state (for Form components)
  useEffect(() => {
    form.setValue("artist_id", artistId);
    form.setValue("title", title);
    form.setValue("body", body);
    form.setValue("body_rendered", bodyRendered);
    form.setValue("user_id", userId);
  }, [artistId, title, body, bodyRendered, userId, form]);

  const validateForm = (): boolean => {
    const formData: TabFormData = {
      artist_id: artistId,
      title,
      body,
      body_rendered: bodyRendered,
      user_id: userId,
    };

    const result = tabSchema.safeParse(formData);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.errors.forEach((error) => {
        if (error.path[0]) {
          newErrors[error.path[0].toString()] = error.message;
        }
      });
      setErrors(newErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const formData: TabFormData = {
      artist_id: artistId,
      title,
      body,
      body_rendered: bodyRendered,
      user_id: userId,
    };

    if (initialData) {
      // Update mode - only send changed fields
      const update: TabUpdatePayload = {};
      if (formData.artist_id !== initialData.artist_id)
        update.artist_id = formData.artist_id;
      if (formData.title !== initialData.title) update.title = formData.title;
      if (formData.body !== initialData.body) update.body = formData.body;
      if (formData.body_rendered !== initialData.body_rendered)
        update.body_rendered = formData.body_rendered;
      if (formData.user_id !== initialData.user_id)
        update.user_id = formData.user_id;
      onSubmit(update);
    } else {
      // Create mode
      onSubmit({
        artist_id: formData.artist_id,
        title: formData.title,
        body: formData.body,
        body_rendered: formData.body_rendered || undefined,
        user_id: formData.user_id,
      });
    }
  };

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        .shimmer {
          background: linear-gradient(
            90deg,
            hsl(var(--muted)) 0%,
            hsl(var(--muted) / 0.6) 50%,
            hsl(var(--muted)) 100%
          );
          background-size: 200% 100%;
          animation: shimmer 1.5s ease-in-out infinite;
        }
      `}</style>
      <Form {...form}>
        <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0">
          {/* Title and Artist in a row */}
          <div className="grid grid-cols-2 gap-4 flex-shrink-0">
            <FormItem>
              <FormLabel>{t("tabs.titleLabel")}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t("tabs.titlePlaceholder")}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </FormControl>
              {errors.title && <FormMessage>{errors.title}</FormMessage>}
            </FormItem>

            <FormItem>
              <FormLabel>{t("tabs.artistLabel")}</FormLabel>
              <FormControl>
                {artistsLoading || artistsError ? (
                  <div className="h-10 w-full rounded-md border border-input shimmer" />
                ) : (
                  <Select
                    value={artistId ? String(artistId) : ""}
                    onValueChange={(value) => {
                      setArtistId(value ? Number(value) : 0);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("tabs.artistPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {artists?.list?.map((artist) => (
                        <SelectItem key={artist.id} value={String(artist.id)}>
                          {artist.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </FormControl>
              {errors.artist_id && (
                <FormMessage>{errors.artist_id}</FormMessage>
              )}
            </FormItem>
          </div>

          {/* Body and Body Rendered side by side */}
          <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 mt-4">
            <div className="flex flex-col h-full min-h-0">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 flex-shrink-0">
                {t("tabs.bodyLabel")}
              </label>
              <textarea
                className="flex-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none min-h-0"
                placeholder={t("tabs.bodyPlaceholder")}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              {errors.body && (
                <p className="text-sm font-medium text-destructive mt-1 flex-shrink-0">
                  {errors.body}
                </p>
              )}
            </div>

            <div className="flex flex-col h-full min-h-0">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 flex-shrink-0">
                {t("tabs.bodyRenderedLabel")}
              </label>
              <div
                className="flex-1 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm font-mono overflow-auto min-h-0"
                dangerouslySetInnerHTML={{ __html: bodyRendered || "" }}
              />
              {errors.body_rendered && (
                <p className="text-sm font-medium text-destructive mt-1 flex-shrink-0">
                  {errors.body_rendered}
                </p>
              )}
            </div>
          </div>

          {/* User selection */}
          <div className="flex-shrink-0 mt-4">
            <FormItem>
              <FormLabel>{t("tabs.userLabel")}</FormLabel>
              <FormControl>
                {usersLoading || usersError ? (
                  <div className="h-10 w-full rounded-md border border-input shimmer" />
                ) : (
                  <Select
                    value={userId ? String(userId) : ""}
                    onValueChange={(value) => {
                      setUserId(value ? Number(value) : 0);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("tabs.userPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {users?.list?.map((user) => (
                        <SelectItem key={user.id} value={String(user.id)}>
                          {user.name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </FormControl>
              {errors.user_id && <FormMessage>{errors.user_id}</FormMessage>}
            </FormItem>
          </div>

          <div className="flex justify-end gap-2 flex-shrink-0 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading
                ? t("common.saving")
                : initialData
                ? t("common.update")
                : t("common.create")}
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
}
